import DatePicker from "react-datepicker";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import React, { FC, Fragment, useState } from "react";
import DatePickerCustomHeaderTwoMonth from "./DatePickerCustomHeaderTwoMonth";
import DatePickerCustomDay from "./DatePickerCustomDay";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import GuestsInput from "./HeroSearchForm2Mobile/GuestsInput";


interface ModalSelectDateProps {
  renderChildren?: (p: { openModal: () => void }) => React.ReactNode;
  onClose?: () => void; // Agregamos la prop onClose
}

const ModalSelectDate: FC<ModalSelectDateProps> = ({ renderChildren, onClose }) => {
  const [showModal, setShowModal] = useState(false);

  const getDateFromStorage = (key: string): Date | null => {
    const storedDate = sessionStorage.getItem(key);
    if (storedDate) {
      const [year, month, day] = storedDate.split('-').map(num => parseInt(num, 10));
      return new Date(year, month - 1, day); // El mes es 0-indexado en JavaScript
    }
    return null;
  };

  const storedStartDate = getDateFromStorage("start_disp");
  const storedEndDate = getDateFromStorage("end_disp");

  const [startDate, setStartDate] = useState<Date | null>(storedStartDate || new Date());
  
  // Si no hay una fecha de endDate en sessionStorage, calcula la fecha de checkout como hoy + 3 días.
  const defaultCheckoutDate = new Date();
  defaultCheckoutDate.setDate(defaultCheckoutDate.getDate() + 3);
  
  const [endDate, setEndDate] = useState<Date | null>(storedEndDate || defaultCheckoutDate);

  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Los meses son 0-indexados, por lo tanto, sumamos 1
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    // Guardar en sessionStorage en formato YYYY-MM-DD
    sessionStorage.setItem("start_disp", formatDate(start));
    sessionStorage.setItem("end_disp", formatDate(end));
    sessionStorage.setItem("selected_date", `${formatDate(start)} - ${formatDate(end)}`);
  };

  const closeModal = () => {
    setShowModal(false);
    if (onClose) {
      onClose(); // Llamamos a la función onClose cuando se cierra el modal
    }
  };

  const openModal = () => {
    setShowModal(true);
  };

  const renderButtonOpenModal = () => {
    return renderChildren ? (
      renderChildren({ openModal })
    ) : (
      <button onClick={openModal}>Select Date</button>
    );
  };

  return (
    <>
      {renderButtonOpenModal()}
      <Transition appear show={showModal} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={closeModal}
        >
          <div className="fixed inset-0 bg-neutral-100 dark:bg-neutral-900">
            <div className="flex h-full">
              <Transition.Child
                as={Fragment}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <Dialog.Panel className="relative h-full overflow-hidden flex-1 flex flex-col justify-between ">
                  <>
                    <div className="absolute left-4 top-4">
                      <button
                        className="focus:outline-none focus:ring-0"
                        onClick={closeModal}
                      >
                        <XMarkIcon className="w-5 h-5 text-black dark:text-white" />
                      </button>
                    </div>

                    <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                      <div className="flex-1 flex flex-col bg-white dark:bg-neutral-800">
                        <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                          <div className="p-5 ">
                            <span className="block font-semibold text-xl sm:text-2xl">
                              {`Selecciona la fecha`}
                            </span>
                          </div>
                          <div className="flex-1 relative flex z-10 ">
                            <div className="overflow-hidden rounded-3xl ">
                              <DatePicker
                                selected={startDate}
                                onChange={onChangeDate}
                                startDate={startDate}
                                endDate={endDate}
                                minDate={new Date()}
                                selectsRange
                                monthsShown={1}
                                showPopperArrow={false}
                                inline
                                renderCustomHeader={(p) => (
                                  <DatePickerCustomHeaderTwoMonth {...p} />
                                )}
                                renderDayContents={(day, date) => (
                                  <DatePickerCustomDay
                                    dayOfMonth={day}
                                    date={date}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <GuestsInput />
                    <div className="px-4 py-3 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700 flex justify-between">
                      <button
                        type="button"
                        className="underline font-semibold flex-shrink-0"
                        onClick={() => {
                          onChangeDate([null, null]);
                        }}
                      >
                        Borrar
                      </button>
                      <ButtonPrimary
                        sizeClass="px-6 py-3 !rounded-xl"
                        onClick={closeModal}
                      >
                        Guardar
                      </ButtonPrimary>
                    </div>
                  </>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default ModalSelectDate;